import React from "react";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { HERO_IMAGES } from "@/data/heroImages";

const Section = ({ number, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5 }}
    className="mb-12"
  >
    <div className="flex items-baseline gap-4 mb-5">
      <span className="font-display text-2xl md:text-3xl text-red-500 leading-none">{String(number).padStart(2, "0")}</span>
      <h2 className="font-display text-xl sm:text-2xl md:text-3xl text-navy-700 leading-tight">{title}</h2>
    </div>
    <div className="pl-0 md:pl-12 text-[15px] leading-[1.8] text-[#444] space-y-3">{children}</div>
  </motion.section>
);

const Row = ({ label, value }) => (
  <div className="flex flex-wrap gap-x-3 text-[15px]">
    <span className="font-semibold text-navy-700 min-w-[210px]">{label}</span>
    <span className="text-[#444]">{value}</span>
  </div>
);

export default function MentionsLegalesPage() {
  return (
    <>
      <PageHero
        eyebrow="Informations légales"
        title="Mentions"
        italicWord="légales"
        subtitle="Informations légales concernant l'École de Commerce de Lyon — Campus d'Abidjan, conformément à la loi pour la confiance dans l'économie numérique (LCEN)."
        crumb="Mentions légales"
        image={HERO_IMAGES.legal}
      />

      <section className="section-padding bg-white">
        <div className="container-edge max-w-4xl">
          <Section number={1} title="Éditeur du site">
            <p>Le site est édité par :</p>
            <div className="mt-4 space-y-2 bg-[#FAF8F3] p-5 md:p-6 border-l-2 border-navy-500">
              <Row label="Dénomination sociale" value="ECOLE DE COMMERCE — BSF" />
              <Row label="Forme juridique" value="Société par actions simplifiée (SAS)" />
              <Row label="Capital social" value="50 000 €" />
              <Row label="Siège social" value="84 rue de la Charité, 69002 Lyon, France" />
              <Row label="SIREN" value="793 367 780" />
              <Row label="SIRET" value="793 367 780 00053" />
              <Row label="RCS" value="Lyon" />
              <Row label="TVA intracommunautaire" value="FR93793367780" />
              <Row label="Téléphone (Lyon)" value="+33 4 78 03 40 36" />
            </div>
            <p className="mt-5">Pour le campus d'Abidjan :</p>
            <div className="mt-3 space-y-2 bg-[#FAF8F3] p-5 md:p-6 border-l-2 border-red-500">
              <Row label="Adresse" value="157 Rue Sonhmon (Pharmacie Y4), Abidjan, Côte d'Ivoire" />
              <Row label="Téléphone" value="+225 01 04 41 94 06 / +225 07 49 54 45 02" />
              <Row label="Email" value="contact@ecoledecommercedelyon.ci" />
            </div>
          </Section>

          <Section number={2} title="Directeur de la publication">
            <p>
              Le directeur de la publication est le représentant légal de la société ECOLE DE COMMERCE — BSF.
            </p>
          </Section>

          <Section number={3} title="Organisme de formation">
            <p>
              L'École de Commerce de Lyon est enregistrée en qualité d'organisme de formation sous le numéro
              {" "}<strong className="text-navy-700">84692279069</strong> auprès du préfet de la région Auvergne-Rhône-Alpes.
            </p>
            <p>Cet enregistrement ne vaut pas agrément de l'État.</p>
          </Section>

          <Section number={4} title="Certification qualité">
            <p>
              L'École de Commerce de Lyon est certifiée <strong className="text-navy-700">Qualiopi</strong> pour ses actions de formation, de VAE et d'apprentissage.
            </p>
            <p>
              <strong className="text-navy-700">Organisme certificateur :</strong> AFNOR Certification.
            </p>
          </Section>

          <Section number={5} title="Hébergement">
            <div className="space-y-2 bg-[#FAF8F3] p-5 md:p-6 border-l-2 border-navy-500">
              <Row label="Hébergeur" value="OVH SAS" />
              <Row label="Adresse" value="2 rue Kellermann, 59100 Roubaix, France" />
              <Row label="Site internet" value="www.ovh.com" />
            </div>
          </Section>

          <Section number={6} title="Propriété intellectuelle">
            <p>
              L'ensemble des contenus présents sur le site de l'École de Commerce de Lyon — Campus Abidjan
              (textes, images, graphismes, logos, vidéos, documents téléchargeables, etc.) sont protégés
              par le droit de la propriété intellectuelle.
            </p>
            <p>
              Toute reproduction, représentation, diffusion ou exploitation, totale ou partielle, sans
              autorisation écrite préalable est strictement interdite.
            </p>
          </Section>

          <Section number={7} title="Données personnelles">
            <p>
              Les données personnelles collectées via ce site sont traitées conformément à la réglementation
              relative à la protection des données personnelles (RGPD).
            </p>
            <p>
              <strong className="text-navy-700">Délégué à la protection des données :</strong>{" "}
              <a href="mailto:dpd@eclyon.fr" className="text-red-500 hover:underline">dpd@eclyon.fr</a>
            </p>
            <p>
              Pour en savoir plus, consultez notre{" "}
              <a href="/politique-de-confidentialite" className="text-red-500 hover:underline">
                politique de confidentialité
              </a>.
            </p>
          </Section>

          <Section number={8} title="Réclamations">
            <p>
              Toute réclamation peut être adressée à l'École de Commerce de Lyon à l'adresse suivante :
            </p>
            <p>
              <a href="mailto:qualite@eclyon.fr" className="text-red-500 hover:underline">qualite@eclyon.fr</a>
            </p>
          </Section>

          <Section number={9} title="Médiation de la consommation">
            <p>
              Conformément aux articles L611-1 et R612-1 du Code de la consommation, tout consommateur peut
              recourir gratuitement à un médiateur de la consommation.
            </p>
            <p>
              Si une réclamation écrite adressée à l'École de Commerce de Lyon n'a pas permis de résoudre le
              litige dans un délai de deux mois, le consommateur peut saisir le médiateur suivant :
            </p>
            <div className="mt-3 space-y-1.5 bg-[#FAF8F3] p-5 md:p-6 border-l-2 border-navy-500">
              <div className="font-display text-lg text-navy-700">MCP Médiation</div>
              <div className="text-sm text-[#555]">Médiation de la Consommation &amp; Patrimoine</div>
              <div className="text-sm text-[#555]">12 Square Desnouettes, 75015 Paris</div>
              <div className="text-sm">
                <a href="https://www.mcpmediation.org" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:underline">
                  www.mcpmediation.org
                </a>
              </div>
            </div>
          </Section>

          <p className="text-xs text-navy-500 mt-12 pt-6 border-t border-navy-100">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
          </p>
        </div>
      </section>
    </>
  );
}
